import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import ErrorMessage from '@/components/common/ErrorMessage';
import { IDeleteResponse } from '@/interfaces/common.model';
import {
  ISalaryHeadConfig,
  ISalaryHeadDetailResponse,
} from '@/interfaces/salary.model';
import { showToastNotification } from '@/utility/helper';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap';

// ─── Types ───────────────────────────────────────────────────────────────────

type THeadType = 'earning' | 'deduction' | '';

interface IManualAdjustmentForm {
  headType: THeadType;
  siteSalaryHeadId: string;
  amount: number | '';
}

export interface IManualSalaryAdjustmentModalProps {
  isOpen: boolean;
  userId: string;
  siteId: string; // from useParams in PayslipOfficeWise
  startDate: string; // pageState.sortByDate[0]
  onClose: () => void;
  onSuccess: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

const ManualSalaryAdjustmentModal = ({
  isOpen,
  userId,
  siteId,
  startDate,
  onClose,
  onSuccess,
}: IManualSalaryAdjustmentModalProps) => {
  // All site salary heads fetched once when modal opens
  const [allSalaryHeads, setAllSalaryHeads] = useState<ISalaryHeadConfig[]>([]);
  const [isHeadsLoading, setIsHeadsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IManualAdjustmentForm>({
    defaultValues: { headType: '', siteSalaryHeadId: '', amount: '' },
  });

  const headType = watch('headType');

  // Salary heads filtered by currently selected type
  const filteredHeads = allSalaryHeads.filter(
    (item) => item?.salaryhead?.type === headType,
  );

  // ─── Fetch site salary heads on modal open ────────────────────────────────
  useEffect(() => {
    if (!isOpen || !siteId) return;

    const fetchSiteSalaryHeads = async () => {
      try {
        setIsHeadsLoading(true);
        const response: ISalaryHeadDetailResponse = await callApi(
          API_URL + API_ENDPOINT.getSiteSalaryHeadDetail + `?siteId=${siteId}`,
          { method: 'GET' },
        );
        if (response?.status) {
          setAllSalaryHeads(response?.data ?? []);
        }
      } catch (error) {
        if (error && typeof error === 'object' && 'message' in error) {
          showToastNotification('danger', (error as IApiErrorResponse).message);
        } else {
          showToastNotification('danger', 'Failed to load salary heads.');
        }
      } finally {
        setIsHeadsLoading(false);
      }
    };

    fetchSiteSalaryHeads();
  }, [isOpen, siteId]);

  // ─── When type changes, reset the salary head selection ──────────────────
  const handleHeadTypeChange = (type: THeadType) => {
    setValue('headType', type);
    setValue('siteSalaryHeadId', '');
  };

  // ─── Close & reset ────────────────────────────────────────────────────────
  const handleClose = () => {
    reset();
    setAllSalaryHeads([]);
    onClose();
  };

  // ─── Submit ───────────────────────────────────────────────────────────────
  const onSubmit: SubmitHandler<IManualAdjustmentForm> = async (data) => {
    try {
      setIsSubmitting(true);
      const API = API_URL + API_ENDPOINT.manualSalaryAdjustment + userId;
      const response: IDeleteResponse = await callApi(API, {
        method: 'POST',
        data: {
          startDate,
          siteSalaryHeadId: data.siteSalaryHeadId,
          amount: Number(data.amount),
        },
      });

      if (response?.status) {
        showToastNotification(
          'success',
          response?.message || 'Salary adjusted successfully.',
        );
        handleClose();
        onSuccess();
      } else {
        showToastNotification(
          'danger',
          response?.message || 'Something went wrong.',
        );
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="md">
      <ModalHeader toggle={handleClose}>
        <h3 className="mb-0">Manual Salary Adjustment</h3>
      </ModalHeader>

      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody className="p-4">
          {isHeadsLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <p className="mt-2 text-muted">Loading salary heads...</p>
            </div>
          ) : (
            <div className="row g-3">
              {/* ── Salary Head Type ──────────────────────────────── */}
              <div className="col-12">
                <label htmlFor="headType" className="form-label">
                  Salary Head Type <span className="text-danger">*</span>
                </label>
                <select
                  id="headType"
                  className={`form-select ${errors.headType ? 'is-invalid' : ''}`}
                  {...register('headType', {
                    required: 'Please select a salary head type.',
                  })}
                  onChange={(e) =>
                    handleHeadTypeChange(e.target.value as THeadType)
                  }
                >
                  <option value="">-- Select Type --</option>
                  <option value="earning">Earning</option>
                  <option value="deduction">Deduction</option>
                </select>
                <ErrorMessage message={errors.headType?.message} />
              </div>

              {/* ── Salary Head ───────────────────────────────────── */}
              <div className="col-12">
                <label htmlFor="siteSalaryHeadId" className="form-label">
                  Salary Head <span className="text-danger">*</span>
                </label>
                <select
                  id="siteSalaryHeadId"
                  className={`form-select ${errors.siteSalaryHeadId ? 'is-invalid' : ''}`}
                  disabled={!headType}
                  {...register('siteSalaryHeadId', {
                    required: 'Please select a salary head.',
                  })}
                >
                  <option value="">
                    {!headType
                      ? 'Select a type first'
                      : filteredHeads.length === 0
                        ? 'No heads available for this type'
                        : '-- Select Salary Head --'}
                  </option>
                  {filteredHeads.map((head) => (
                    <option key={head.id} value={head.id}>
                      {head?.salaryhead?.name}
                    </option>
                  ))}
                </select>
                <ErrorMessage message={errors.siteSalaryHeadId?.message} />
              </div>

              {/* ── Amount ───────────────────────────────────────── */}
              <div className="col-12">
                <label htmlFor="amount" className="form-label">
                  Amount <span className="text-danger">*</span>
                </label>
                <input
                  id="amount"
                  type="number"
                  step="any"
                  min={0.01}
                  className={`form-control ${errors.amount ? 'is-invalid' : ''}`}
                  placeholder="Enter amount"
                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  onInput={(e) => {
                    e.currentTarget.value = e.currentTarget.value.replace(
                      /-/g,
                      '',
                    );
                  }}
                  {...register('amount', {
                    required: 'Amount is required.',
                    min: {
                      value: 0.01,
                      message: 'Amount must be greater than 0.',
                    },
                    valueAsNumber: true,
                  })}
                />
                <ErrorMessage message={errors.amount?.message} />
              </div>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting || isHeadsLoading}
          >
            {isSubmitting ? <Spinner size="sm" /> : 'Submit'}
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default ManualSalaryAdjustmentModal;
